import { useEffect, useState, useCallback } from "react";
import { Params, useParams, useNavigate } from "react-router-dom";
import { Button, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as TivaPlaceholder } from "../../../assets/placeholder-tiva.svg";
import { ReactComponent as UploadIcon } from "../../../assets/cloudUploadIcon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/trash.icon.svg";
import { ReactComponent as RefreshIcon } from "../../../assets/refreshIcon.svg";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { ProvidersContainer } from "../../../common/containers/providers/providers.container";
import {
    fetchContentFolders,
    fetchLearningObject,
    fetchLearningObjects,
    deleteLearningObjects
} from "../../../api/admin/content-providers/endpoints-referrers";
import { publishRecognitionResult } from "../../../api/admin/recognition-result/endpoints-referrers";
import { ProviderType } from "../../../common/enums/provider-type.enum";
import { Provider } from "../../../common/interfaces/admin/provider.interface";
import { Folder } from "../../../common/interfaces/admin/folder.interface";
import { RecognitionResult } from "../../../common/interfaces/admin/recognition-result.interface";
import {
    TableLearningObject,
    LearningObject
} from "../../../common/interfaces/admin/learning-object.interface";
import EmptyLeftMenuLayout from "../../../components/layout/empty-left-menu-layout.comp";
import HeaderLayout from "../../../components/layout/header-layout.comp";
import { LoaderView } from "../../../components/loader/loader.comp";
import { ActionMenu } from "../../../components/menu-filter/menu-filter.comp";
import CoursesTree from "../../../components/admin/courses-tree/courses-tree.comp";
import LearningObjects from "../../../components/admin/learning-objects/learning-objects.comp";
import FileUploadView from "../file-upload/file-upload";
import { ModalTemplate } from "../../../components/modal-template/modal-template.comp";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../../components/confirmation-popup/confirmation-popup.comp";

const KnowledgeBaseElements = () => {
    const DEFAULT_PAGE_INDEX = 1;
    const DEFAULT_PAGE_SIZE = 50;

    const navigate = useNavigate();
    const { providerId, courseId } = useParams<Params<string>>();
    const { user } = FirebaseAuthContainer.useContainer();
    const { providers } = ProvidersContainer.useContainer();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [currentCourse, setCurrentCourse] = useState<Folder>();
    const [folderTree, setFolderTree] = useState<Folder[]>([]);
    const [learningObjects, setLearningObjects] = useState<TableLearningObject[]>([]);
    const [checkedLOs, setCheckedLOs] = useState<TableLearningObject[]>([]);
    const [selectionMode, setSelectionMode] = useState<boolean>(false);
    const [deletableUids, setDeletableUids] = useState<string[]>([]);
    const [loTotalCount, setLoTotalCount] = useState<number>(0);
    const [pageIndex, setPageIndex] = useState<number>(DEFAULT_PAGE_INDEX);
    const [editableRR, setEditableRR] = useState<RecognitionResult>();
    const [filesUploadOpen, setFilesUploadOpen] = useState<boolean>(false);
    const [deleteLOsConfirmationOpen, setDeleteLOsConfirmationOpen] = useState<boolean>(false);
    const [publishConfirmationOpen, setPublishConfirmationOpen] = useState<boolean>(false);
    const [unpublishConfirmationOpen, setUnpublishConfirmationOpen] = useState<boolean>(false);
    const [publishSubtitle, setPublishSubtitle] = useState<string>();
    const publishDescription = `When you publish one version of the document,
    all other versions will be unpublished. Knowledge Base will use published
    version for answering questions regarding this document.`;
    const unpublishDescription = `When you unpublish all documents, Knowledge Base will no longer be
    able to answer questions regarding this document.`;

    useEffect(() => {
        if (!providers.length || providerId) return;

        const provider =
            providers.find(provider => provider.name === ProviderType.docebo) ??
            providers.find(provider => provider.name === ProviderType.fileUploader);

        if (provider) {
            navigate(`/knowledge-base/${provider.id}`);
        }
    }, [navigate, providers, providerId]);

    useEffect(() => {
        const currentProviderId = Number(providerId);
        if (!user || !providers.length || isNaN(currentProviderId)) return;

        if (!providers.find(provider => provider.id === currentProviderId)) {
            navigate("/knowledge-base");
            return;
        }

        setSelectionMode(
            providers.find(
                provider =>
                    provider.id === currentProviderId && provider.name === ProviderType.fileUploader
            ) !== undefined
        );

        fetchContentFolders(user.companyId, currentProviderId).then(response => {
            // TODO response type will be changed
            setFolderTree(response.data.length ? response.data[0].courses : []);

            const provider: Provider = response.data.length ? response.data[0] : undefined;
            if (
                provider?.name == ProviderType.fileUploader &&
                provider?.courses?.find(course => course.courseId === "0")
            ) {
                navigate(`/knowledge-base/${providerId}/0`);
            }
        });
    }, [navigate, user, providerId, providers]);

    useEffect(() => {
        if (user && courseId) {
            loadLearningObjects(
                user.companyId,
                courseId,
                createQuery(DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, courseId === "0") // TODO temporary solution to sort by date
            );
        } else {
            setLearningObjects([]);
        }
    }, [user, courseId]);

    useEffect(() => {
        setCurrentCourse(
            courseId && !!folderTree.length
                ? folderTree.find(item => item.courseId === courseId)
                : undefined
        );
    }, [courseId, folderTree]);

    const createQuery = (page: number, pageSize: number, sortByDate: boolean) => {
        const queryParts: string[] = [];

        queryParts.push(`page=${page}`);
        queryParts.push(`pageSize=${pageSize}`);

        // TODO temporary solution to sort by date
        queryParts.push(`sortBy=${sortByDate ? "createdAt" : "name"}`);
        queryParts.push(`sortOrder=${sortByDate ? "desc" : "asc"}`);

        return `?${queryParts.join("&")}`;
    };

    const loadLearningObjects = (companyId: number, courseId: string, query?: string) => {
        setIsLoading(true);
        fetchLearningObjects(companyId, courseId, query).then(response => {
            const {
                data: { count, items }
            } = response;
            setLoTotalCount(count);
            setLearningObjects(items.map((item: LearningObject) => ({ ...item, showRR: false })));
            setPageIndex(DEFAULT_PAGE_INDEX);
            setIsLoading(false);
        });
    };

    const appendLearningObjects = (companyId: number, courseId: string, query?: string) => {
        fetchLearningObjects(companyId, courseId, query).then(response => {
            const {
                data: { count, items }
            } = response;
            setLoTotalCount(count);
            setLearningObjects(prevItems => [
                ...prevItems,
                ...items.map((item: LearningObject) => ({ ...item, showRR: false }))
            ]);
            setPageIndex(prevIndex => prevIndex + 1);
        });
    };

    const handleAppendLearningObjects = useCallback(() => {
        if (!user || !courseId) return;

        appendLearningObjects(
            user.companyId,
            courseId,
            createQuery(pageIndex + 1, DEFAULT_PAGE_SIZE, courseId === "0") // TODO temporary solution to sort by date
        );
    }, [user, courseId, pageIndex]);

    const handleChangePublishStatus = (rr: RecognitionResult, loName: string) => {
        setEditableRR(rr);
        setPublishSubtitle(`${loName} : ${rr.name} v.${rr.version}`);
        rr.isPublish ? setUnpublishConfirmationOpen(true) : setPublishConfirmationOpen(true);
    };

    const handleDeleteCheckedLearningObjects = () => {
        const uids = checkedLOs.map((item: LearningObject) => item.loItemId);
        if (uids.length > 0) {
            handleDeleteLearningObjects(uids);
        }
    };

    const handleDeleteLearningObjects = (uids: string[]) => {
        setDeletableUids(uids);
        setDeleteLOsConfirmationOpen(true);
    };

    const handleDeleteLOsConfirmationAction = () => {
        if (!user || !courseId || !deletableUids.length) return;

        deleteLearningObjects(user.companyId, courseId, deletableUids).then(() => {
            setLearningObjects(prevLOs =>
                prevLOs.filter(lo => !deletableUids.find(uid => uid === lo.loItemId))
            );
            setLoTotalCount(prevValue => prevValue - deletableUids.length);
            setCheckedLOs([]);
        });

        setDeleteLOsConfirmationOpen(false);
    };

    const handlePublishConfirmationAction = () => {
        if (!user || !courseId || !editableRR) return;

        publishRecognitionResult(user.companyId, editableRR.uid, true).then(() => {
            fetchLearningObject(user.companyId, courseId, editableRR.loItemId).then(response => {
                const currentLO = response.data;
                setLearningObjects(loList =>
                    loList.map(lo =>
                        lo.loItemId === editableRR.loItemId
                            ? {
                                  ...lo,
                                  status: currentLO.status,
                                  recognitionResults: currentLO.recognitionResults
                              }
                            : lo
                    )
                );
            });
        });
        setPublishConfirmationOpen(false);
    };

    const handleUnpublishConfirmationAction = () => {
        if (!user || !courseId || !editableRR) return;

        publishRecognitionResult(user.companyId, editableRR.uid, false).then(() => {
            fetchLearningObject(user.companyId, courseId, editableRR.loItemId).then(response => {
                const currentLO = response.data;
                setLearningObjects(loList =>
                    loList.map(lo =>
                        lo.loItemId === editableRR.loItemId
                            ? {
                                  ...lo,
                                  status: currentLO.status,
                                  recognitionResults: currentLO.recognitionResults
                              }
                            : lo
                    )
                );
            });
        });
        setUnpublishConfirmationOpen(false);
    };

    const selectCourse = (courseId: string) => {
        navigate(`/knowledge-base/${providerId}/${courseId}`);
    };

    const viewRecognitionResult = (uid: string) => {
        navigate(`/recognition-result/${providerId}/${uid}`);
    };

    const handleFilesUpload = () => {
        setFilesUploadOpen(true);
    };

    const handleFilesUploadClose = (needUpdate: boolean) => {
        setFilesUploadOpen(false);

        if (needUpdate && user && courseId) {
            loadLearningObjects(
                user.companyId,
                courseId,
                createQuery(DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, courseId === "0") // TODO temporary solution to sort by date
            );
        }
    };

    return (
        <EmptyLeftMenuLayout
            leftMenu={
                <CoursesTree
                    list={folderTree}
                    selectedCourse={courseId}
                    selectCourse={selectCourse}
                />
            }
        >
            <HeaderLayout blurHeader={false}>
                <div className="knowledge-base">
                    {currentCourse && (
                        <div className="knowledge-base-title">
                            <span className="knowledge-base-title__text">
                                {currentCourse.courseId === "0"
                                    ? "Uploaded files"
                                    : currentCourse.name}
                            </span>
                        </div>
                    )}
                    {selectionMode && (
                        <div className="knowledge-base-filters">
                            {!!learningObjects.length && (
                                <div className="knowledge-base-filter knowledge-base-filter--actions">
                                    <ActionMenu
                                        label="Action"
                                        disabled={checkedLOs.length === 0}
                                        actions={[
                                            {
                                                name: "Delete",
                                                action: handleDeleteCheckedLearningObjects
                                            }
                                        ]}
                                        minWidth={160}
                                    />
                                </div>
                            )}
                            {courseId && (
                                <>
                                    <div className="knowledge-base-filter-group"></div>
                                    <Button
                                        className="knowledge-base-upload-button"
                                        variant="text"
                                        startIcon={
                                            <SvgIcon
                                                className="knowledge-base-upload-button__icon"
                                                component={UploadIcon}
                                                inheritViewBox
                                            />
                                        }
                                        onClick={handleFilesUpload}
                                    >
                                        Upload File(s)
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                    <div className="knowledge-base-table">
                        <LearningObjects
                            totalCount={loTotalCount}
                            items={learningObjects}
                            setItems={setLearningObjects}
                            checkedItems={checkedLOs}
                            setCheckedItems={setCheckedLOs}
                            selectionMode={selectionMode}
                            placeholder={TivaPlaceholder}
                            handleAppendFiles={handleAppendLearningObjects}
                            handleChangePublishStatus={handleChangePublishStatus}
                            handleAIResult={viewRecognitionResult}
                            handleDelete={handleDeleteLearningObjects}
                        />
                        <LoaderView show={isLoading} />
                    </div>
                </div>
                <ModalTemplate
                    open={filesUploadOpen}
                    width={658}
                    height={466}
                    handleClose={handleFilesUploadClose}
                >
                    <FileUploadView handleClose={handleFilesUploadClose} />
                </ModalTemplate>
                <ConfirmationPopup
                    open={deleteLOsConfirmationOpen}
                    type={ConfirmationPopupType.dangerous}
                    icon={TrashIcon}
                    title="Delete"
                    description={`You are trying to delete ${deletableUids.length} learning object(s).
                                This can't be undone!`}
                    actionName="Delete"
                    handleAction={handleDeleteLOsConfirmationAction}
                    handleClose={() => setDeleteLOsConfirmationOpen(false)}
                />
                <ConfirmationPopup
                    open={publishConfirmationOpen}
                    icon={RefreshIcon}
                    title="Publish"
                    subtitle={publishSubtitle}
                    description={publishDescription}
                    actionName="Publish"
                    showCancelButton={false}
                    handleAction={handlePublishConfirmationAction}
                    handleClose={() => setPublishConfirmationOpen(false)}
                />
                <ConfirmationPopup
                    open={unpublishConfirmationOpen}
                    icon={RefreshIcon}
                    title="Unpublish"
                    subtitle={publishSubtitle}
                    description={unpublishDescription}
                    actionName="Unpublish"
                    showCancelButton={false}
                    handleAction={handleUnpublishConfirmationAction}
                    handleClose={() => setUnpublishConfirmationOpen(false)}
                />
            </HeaderLayout>
        </EmptyLeftMenuLayout>
    );
};

export default KnowledgeBaseElements;
